import { Pane } from 'tweakpane'
import { createDriftpane } from '@niccolofanton/driftpane'
import { defaultParams, STORAGE_KEY, FOLDERS_KEY } from '../config/defaults.js'
import { params, saveParams, loadFolderState, saveFolderState } from '../config/persistence.js'
import { builtinPresets, userPresets, saveUserPresets } from '../config/presets.js'

export const initControls = ({ renderer, controls, ditherPass, lightformers, carModel, scene, markDirty }) => {
    const pane = new Pane({ title: 'Controls' })

    const savedFolders = loadFolderState()
    const folderExpanded = (key, fallback = true) => savedFolders[key] !== undefined ? savedFolders[key] : fallback

    const hexToVec3 = (hex) => {
        const r = parseInt(hex.slice(1, 3), 16) / 255
        const g = parseInt(hex.slice(3, 5), 16) / 255
        const b = parseInt(hex.slice(5, 7), 16) / 255
        return { x: r, y: g, z: b }
    }

    // Cache mesh materials to avoid traverse on every change
    const meshMaterials = []
    if (carModel) {
        carModel.traverse((child) => {
            if (child.isMesh && child.material) meshMaterials.push(child.material)
        })
    }

    const applyParams = () => {
        // Scene
        scene.background.set(params.backgroundColor)

        // Controls
        controls.enableRotate = params.enableRotate
        controls.enableZoom = params.enableZoom
        controls.enablePan = params.enablePan
        controls.autoRotate = params.autoRotate
        controls.autoRotateSpeed = params.autoRotateSpeed

        // Lightformers
        if (lightformers.setSpeed) lightformers.setSpeed(params.lightformerSpeed)
        for (let i = 0; i < meshMaterials.length; i++) {
            meshMaterials[i].envMapIntensity = params.envMapIntensity
        }

        // Shader uniforms
        const u = ditherPass.uniforms
        u.uNoiseScale.value = params.noiseScale
        u.uNoiseSpeed.value = params.noiseSpeed
        u.uNoiseThreshold.value = params.noiseThreshold
        u.uNoiseSoftness.value = params.noiseSoftness
        u.uPixelSize.value = params.pixelSize
        u.uMode.value = params.mode
        u.uToonEnabled.value = params.toonEnabled ? 1.0 : 0.0
        u.uToonLevels.value = params.toonLevels
        const c1 = hexToVec3(params.toonColor1); u.uToonColor1.value.set(c1.x, c1.y, c1.z)
        const c2 = hexToVec3(params.toonColor2); u.uToonColor2.value.set(c2.x, c2.y, c2.z)
        const c3 = hexToVec3(params.toonColor3); u.uToonColor3.value.set(c3.x, c3.y, c3.z)
        const c4 = hexToVec3(params.toonColor4); u.uToonColor4.value.set(c4.x, c4.y, c4.z)
        u.uShowPixelUnderneath.value = params.showPixelUnderneath ? 1.0 : 0.0
        u.uCharBrightnessDelta.value = params.charBrightnessDelta
        u.uCharDarker.value = params.charDarker ? 1.0 : 0.0

        pane.refresh()
    }

    // --- Preset ---
    const presetFolder = pane.addFolder({ title: 'Preset', expanded: folderExpanded('presetFolder') })

    const rebuildPresetButtons = () => {
        const children = [...presetFolder.children]
        children.forEach(c => presetFolder.remove(c))

        for (const [name, preset] of Object.entries(builtinPresets)) {
            presetFolder.addButton({ title: name }).on('click', () => {
                Object.assign(params, defaultParams, preset)
                saveParams()
                applyParams()
            })
        }

        presetFolder.addBlade({ view: 'separator' })

        for (const [name, preset] of Object.entries(userPresets)) {
            const btn = presetFolder.addButton({ title: `★ ${name}` }).on('click', () => {
                Object.assign(params, defaultParams, preset)
                saveParams()
                applyParams()
            })
            btn.element.addEventListener('contextmenu', (e) => {
                e.preventDefault()
                if (!confirm(`Delete preset "${name}"?`)) return
                delete userPresets[name]
                saveUserPresets()
                rebuildPresetButtons()
            })
        }

        presetFolder.addBlade({ view: 'separator' })
        presetFolder.addButton({ title: '+ Save current preset' }).on('click', () => {
            const name = prompt('Preset name:')
            if (!name || !name.trim()) return
            const trimmed = name.trim()
            if (builtinPresets[trimmed]) {
                alert('You cannot overwrite a built-in preset.')
                return
            }
            if (userPresets[trimmed] && !confirm(`Preset "${trimmed}" already exists. Overwrite?`)) return
            userPresets[trimmed] = { ...params }
            saveUserPresets()
            rebuildPresetButtons()
        })
    }

    rebuildPresetButtons()

    // --- Scene ---
    const sceneFolder = pane.addFolder({ title: 'Scene', expanded: folderExpanded('sceneFolder') })
    sceneFolder.addBinding(params, 'backgroundColor', { label: 'Background', view: 'color' })
        .on('change', (e) => { scene.background.set(e.value) })
    sceneFolder.addBinding(params, 'exposure', { label: 'Exposure', min: 0.1, max: 5, step: 0.01 })

    // --- Camera ---
    const cameraFolder = pane.addFolder({ title: 'Camera', expanded: folderExpanded('cameraFolder', false) })
    cameraFolder.addBinding(params, 'enableRotate', { label: 'Rotate' })
        .on('change', (e) => { controls.enableRotate = e.value })
    cameraFolder.addBinding(params, 'enableZoom', { label: 'Zoom' })
        .on('change', (e) => { controls.enableZoom = e.value })
    cameraFolder.addBinding(params, 'enablePan', { label: 'Pan' })
        .on('change', (e) => { controls.enablePan = e.value })
    cameraFolder.addBinding(params, 'autoRotate', { label: 'Auto-rotate' })
        .on('change', (e) => { controls.autoRotate = e.value })
    cameraFolder.addBinding(params, 'autoRotateSpeed', { label: 'Rotation speed', min: 0, max: 10, step: 0.1 })
        .on('change', (e) => { controls.autoRotateSpeed = e.value })

    // --- Lightformers ---
    const lightFolder = pane.addFolder({ title: 'Lights / Reflections', expanded: folderExpanded('lightFolder', false) })
    lightFolder.addBinding(params, 'lightformerSpeed', { label: 'Light speed', min: 0, max: 5, step: 0.01 })
        .on('change', (e) => { if (lightformers.setSpeed) lightformers.setSpeed(e.value) })
    lightFolder.addBinding(params, 'envMapIntensity', { label: 'Reflection intensity', min: 0, max: 5, step: 0.01 })
        .on('change', (e) => {
            for (let i = 0; i < meshMaterials.length; i++) {
                meshMaterials[i].envMapIntensity = e.value
            }
        })

    // --- Noise Mask ---
    const noiseFolder = pane.addFolder({ title: 'Noise Mask', expanded: folderExpanded('noiseFolder') })
    const maskModelBinding = noiseFolder.addBinding(params, 'maskModelOnly', { label: 'Model only' })
    noiseFolder.addBinding(params, 'materialMode', { label: 'Shader on material' })
        .on('change', (e) => { maskModelBinding.disabled = e.value })
    maskModelBinding.disabled = params.materialMode
    noiseFolder.addBinding(params, 'noiseScale', { label: 'Scale', min: 0.5, max: 15, step: 0.1 })
    noiseFolder.addBinding(params, 'noiseSpeed', { label: 'Speed', min: 0.0, max: 3.0, step: 0.01 })
    noiseFolder.addBinding(params, 'noiseThreshold', { label: 'Threshold', min: 0.0, max: 1.0, step: 0.01 })
    noiseFolder.addBinding(params, 'noiseSoftness', { label: 'Softness', min: 0.01, max: 0.5, step: 0.01 })

    // --- Pixelation & Mode ---
    const pixelFolder = pane.addFolder({ title: 'Pixelation / Mode', expanded: folderExpanded('pixelFolder') })
    pixelFolder.addBinding(params, 'pixelSize', { label: 'Pixel size', min: 2, max: 24, step: 1 })
    pixelFolder.addBinding(params, 'mode', { label: 'Mode', options: { 'Dithering': 0, 'ASCII': 1 } })

    // --- Toon ---
    const toonFolder = pane.addFolder({ title: 'Toon Shader', expanded: folderExpanded('toonFolder') })
    toonFolder.addBinding(params, 'toonEnabled', { label: 'Enabled' })
    toonFolder.addBinding(params, 'toonLevels', { label: 'Levels', min: 2, max: 8, step: 1 })
    toonFolder.addBinding(params, 'toonColor1', { label: 'Color 1 (dark)', view: 'color' })
    toonFolder.addBinding(params, 'toonColor2', { label: 'Color 2', view: 'color' })
    toonFolder.addBinding(params, 'toonColor3', { label: 'Color 3', view: 'color' })
    toonFolder.addBinding(params, 'toonColor4', { label: 'Color 4 (light)', view: 'color' })

    // --- Pixel Overlay ---
    const overlayFolder = pane.addFolder({ title: 'Pixel Overlay', expanded: folderExpanded('overlayFolder') })
    overlayFolder.addBinding(params, 'showPixelUnderneath', { label: 'Show pixels underneath' })
    overlayFolder.addBinding(params, 'charBrightnessDelta', { label: 'Brightness delta', min: 0.0, max: 0.8, step: 0.01 })
    overlayFolder.addBinding(params, 'charDarker', { label: 'Darker characters' })

    // --- Mouse Trail ---
    const fluidFolder = pane.addFolder({ title: 'Mouse Trail', expanded: folderExpanded('fluidFolder') })
    fluidFolder.addBinding(params, 'trailRadius', { label: 'Radius', min: 0.02, max: 0.2, step: 0.005 })
    fluidFolder.addBinding(params, 'trailStrength', { label: 'Strength', min: 0.1, max: 3.0, step: 0.05 })
    fluidFolder.addBinding(params, 'trailDissipation', { label: 'Duration (dissipation)', min: 0.95, max: 0.999, step: 0.001 })
    fluidFolder.addBinding(params, 'trailDiffusion', { label: 'Diffusion', min: 0.0, max: 0.4, step: 0.01 })
    fluidFolder.addBinding(params, 'trailSoftness', { label: 'Edge softness', min: 0.0, max: 1.0, step: 0.01 })
    fluidFolder.addBinding(params, 'trailIntensity', { label: 'Effect intensity', min: 0.0, max: 3.0, step: 0.05 })

    // --- Actions ---
    const actionsFolder = pane.addFolder({ title: 'Actions', expanded: folderExpanded('actionsFolder', false) })
    actionsFolder.addButton({ title: 'Export config' }).on('click', () => {
        const json = JSON.stringify(params, null, 2)
        const blob = new Blob([json], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'porsche-shader-config.json'
        a.click()
        URL.revokeObjectURL(url)
    })

    actionsFolder.addButton({ title: 'Reset to defaults' }).on('click', () => {
        if (!confirm('Reset all parameters to their default values?')) return
        Object.assign(params, defaultParams)
        localStorage.removeItem(STORAGE_KEY)
        localStorage.removeItem(FOLDERS_KEY)
        applyParams()
    })

    pane.on('change', () => { saveParams(); if (markDirty) markDirty() })

    // --- Folder state persistence ---
    const allFolders = { presetFolder, sceneFolder, cameraFolder, lightFolder, noiseFolder, pixelFolder, toonFolder, overlayFolder, fluidFolder, actionsFolder }
    const persistFolderState = () => {
        const state = {}
        for (const [key, folder] of Object.entries(allFolders)) {
            state[key] = folder.expanded
        }
        saveFolderState(state)
    }
    for (const folder of Object.values(allFolders)) {
        folder.on('fold', () => { persistFolderState() })
    }

    // --- Driftpane: persistence, draggable/resizable panel, presets ---
    // Tweakpane's importState() re-fires the binding `change` handlers, so the
    // root `pane.on('change')` above (saveParams + markDirty) runs on restore
    // and the render loop re-pushes the uniforms on the next frame. No manual
    // re-apply is needed here.
    createDriftpane(pane, {
        storageNamespace: 'ascii-noise-shader',
        width: 300,
        presetFolderTitle: 'Driftpane',
    })

    return { pane, applyParams }
}
